import { z } from 'zod';
import { UuidSchema, IsoDateSchema, IsoDateTimeSchema } from '../common/ids.js';
import { ShiftStatusSchema } from '../common/enums.js';
import { PaginationQuerySchema } from '../common/pagination.js';
import { DiscoveryQuerySchema } from '../common/discovery.js';
export const ShiftSchema = z.object({
    id: UuidSchema,
    contractId: UuidSchema,
    siteId: UuidSchema,
    zoneId: UuidSchema.nullable(),
    seriesId: UuidSchema.nullable(),
    seriesOccurrenceDate: IsoDateSchema.nullable(),
    slotIndex: z.int().nullable(),
    /** `NULL` = an open slot (02-DATA-MODEL.md §5). */
    assignedUserId: UuidSchema.nullable(),
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
    /** Derived server-side from `starts_at` in the SITE timezone (D-07). */
    businessDate: IsoDateSchema,
    status: ShiftStatusSchema,
    isDetached: z.boolean(),
    cancellationReason: z.string().min(1).nullable(),
    replacedByShiftId: UuidSchema.nullable(),
    createdBy: UuidSchema,
    /**
     * The shift already carries punches, so it is attendance history rather
     * than a plan.
     *
     * The server has always refused to patch, reassign or cancel such a row
     * (`409 SHIFT_HAS_PUNCHES`); it never said so in the row itself, so a client
     * could only find out by trying. A supervisor should see the refusal before
     * they type a cancellation reason, not after.
     */
    hasPunches: z.boolean(),
});
export const ShiftListQuerySchema = DiscoveryQuerySchema.extend(PaginationQuerySchema.shape).extend({
    view: z.enum(['calendar', 'list']).default('list'),
});
/** `POST /shifts` — one-time shift, not tied to a series. */
export const ShiftCreateRequestSchema = z
    .object({
    contractId: UuidSchema,
    siteId: UuidSchema,
    zoneId: UuidSchema.optional(),
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
    assignedUserId: UuidSchema.nullable().optional(),
    /**
     * Phase 1 change batch (2026-08-27), Track B: replacement lineage.
     * When set, the server points the ORIGINAL shift's
     * `replaced_by_shift_id` at the newly created shift — this is the only
     * write path that makes that column reachable.
     */
    replacesShiftId: UuidSchema.optional(),
})
    .refine((v) => new Date(v.endsAt).getTime() > new Date(v.startsAt).getTime(), {
    error: 'endsAt must be strictly after startsAt',
    path: ['endsAt'],
});
/**
 * `PATCH /shifts/:id` — sets `is_detached = true` server-side.
 *
 * `siteId` is here (2026-09-01) so a shift written against the wrong plant can
 * be corrected in place rather than cancelled and retyped. It moves the row
 * **within its own contract only** — `contractId` is deliberately absent,
 * because a shift's contract is the cross-contract isolation boundary the
 * whole system is built to hold, and re-parenting a row across it is not an
 * edit. Moving the site re-derives `business_date` in the NEW site's timezone
 * and re-checks the zone and the assignee against it; a zone that does not
 * belong to the new site is `422 ZONE_NOT_IN_SITE`, and a shift that already
 * carries punches refuses the whole patch as it always did.
 */
export const ShiftPatchRequestSchema = z
    .object({
    siteId: UuidSchema,
    zoneId: UuidSchema.nullable(),
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
    assignedUserId: UuidSchema.nullable(),
})
    .partial()
    .strict();
/** `POST /shifts/:id/assign` — `{ userId }` or `{ userId: null }` to reopen the slot. */
export const ShiftAssignRequestSchema = z.object({
    userId: UuidSchema.nullable(),
});
/** `POST /shifts/:id/cancel` — `reason` mandatory (length not specified in the docs). */
export const ShiftCancelRequestSchema = z.object({
    reason: z.string().trim().min(1),
});
/** `POST /shifts/bulk-assign` — `{ shiftIds[], userId }`. */
export const ShiftBulkAssignRequestSchema = z.object({
    shiftIds: z.array(UuidSchema).min(1),
    userId: UuidSchema,
});
/**
 * `POST /shifts/bulk-assign` response (Phase 1 change batch, 2026-08-27,
 * Track B). Bulk-assign is **all-or-nothing**: either every listed shift is
 * assigned and every updated `Shift` comes back, or the call refuses
 * entirely (e.g. `USER_NOT_ASSIGNABLE`, `WORKER_ALREADY_SCHEDULED`) with a
 * standard error envelope and no shift is assigned. There is no per-item
 * partial-success shape to parse.
 */
export const ShiftBulkAssignResponseSchema = z.array(ShiftSchema);
//# sourceMappingURL=shift.js.map